import { FC, ReactNode, useMemo } from 'react';
import { FlexAlignItems, FlexDirection, FlexJustify } from './types';

export type FlexGridContainerProps = {
  children?: ReactNode;
  wrap?: boolean;
  grow?: boolean;
  fullHeight?: boolean;
  xsGutter?: number;
  smGutter?: number;
  mdGutter?: number;
  lgGutter?: number;
  xlGutter?: number;
  xsDirection?: FlexDirection;
  smDirection?: FlexDirection;
  mdDirection?: FlexDirection;
  lgDirection?: FlexDirection;
  xlDirection?: FlexDirection;
  xsJustify?: FlexJustify;
  smJustify?: FlexJustify;
  mdJustify?: FlexJustify;
  lgJustify?: FlexJustify;
  xlJustify?: FlexJustify;
  xsAlignItems?: FlexAlignItems;
  smAlignItems?: FlexAlignItems;
  mdAlignItems?: FlexAlignItems;
  lgAlignItems?: FlexAlignItems;
  xlAlignItems?: FlexAlignItems;
  fullWidth?: boolean;
  spaceY?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
  xsSpaceY?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
  smSpaceY?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
  mdSpaceY?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
  lgSpaceY?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
  xlSpaceY?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
  xxlSpaceY?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
};

/**
 * A layout container for flexbox based grid layouts.
 *
 * Best used for vertical dimension only layouts and cases where contained elements need to set
 * their size individually with more flexibility.
 *
 * For more information on how css grids and flexbox layouts relate to each other see:
 * https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Grid_Layout/Relationship_of_Grid_Layout
 */
export const FlexGridContainer: FC<FlexGridContainerProps> = ({
  children,
  wrap = true,
  grow = true,
  fullHeight,
  xsGutter,
  smGutter,
  mdGutter,
  lgGutter,
  xlGutter,
  xsDirection,
  smDirection,
  mdDirection,
  lgDirection,
  xlDirection,
  xsJustify,
  smJustify,
  mdJustify,
  lgJustify,
  xlJustify,
  xsAlignItems,
  smAlignItems,
  mdAlignItems,
  lgAlignItems,
  xlAlignItems,
  spaceY,
  xsSpaceY,
  smSpaceY,
  mdSpaceY,
  lgSpaceY,
  xlSpaceY,
  xxlSpaceY,
  fullWidth,
}: FlexGridContainerProps) => {
  const classes = useMemo(
    () =>
      [
        ...(function* () {
          yield 'xl:max-w-[1120px]';
          yield 'flex';
          if (grow) yield 'flex-grow';
          if (wrap) yield 'flex-wrap';
          if (fullHeight) yield 'h-full';
          if (spaceY) yield `space-y-${spaceY}`;
          if (xsSpaceY) yield `space-y-${xsSpaceY}`;
          if (smSpaceY) yield `sm:space-y-${smSpaceY}`;
          if (mdSpaceY) yield `md:space-y-${mdSpaceY}`;
          if (lgSpaceY) yield `lg:space-y-${lgSpaceY}`;
          if (xlSpaceY) yield `xl:space-y-${xlSpaceY}`;
          if (xxlSpaceY) yield `2xl:space-y-${xxlSpaceY}`;
          if (xsGutter) yield `-ml-${xsGutter}`;
          if (smGutter) yield `sm:-ml-${smGutter}`;
          if (mdGutter) yield `md:-ml-${mdGutter}`;
          if (lgGutter) yield `lg:-ml-${lgGutter}`;
          if (xlGutter) yield `xl:-ml-${xlGutter}`;
          if (xsDirection) yield `flex-${xsDirection}`;
          if (smDirection) yield `sm:flex-${smDirection}`;
          if (mdDirection) yield `md:flex-${mdDirection}`;
          if (lgDirection) yield `lg:flex-${lgDirection}`;
          if (xlDirection) yield `xl:flex-${xlDirection}`;
          if (xsJustify) yield `justify-${xsJustify}`;
          if (smJustify) yield `sm:justify-${smJustify}`;
          if (mdJustify) yield `md:justify-${mdJustify}`;
          if (lgJustify) yield `lg:justify-${lgJustify}`;
          if (xlJustify) yield `xl:justify-${xlJustify}`;
          if (xsAlignItems) yield `items-${xsAlignItems}`;
          if (smAlignItems) yield `sm:items-${smAlignItems}`;
          if (mdAlignItems) yield `md:items-${mdAlignItems}`;
          if (lgAlignItems) yield `lg:items-${lgAlignItems}`;
          if (xlAlignItems) yield `xl:items-${xlAlignItems}`;
          if (fullWidth) yield 'w-full';
        })(),
      ].join(' '),
    [
      grow,
      wrap,
      fullHeight,
      xsGutter,
      smGutter,
      mdGutter,
      lgGutter,
      xlGutter,
      xsDirection,
      smDirection,
      mdDirection,
      lgDirection,
      xlDirection,
      xsJustify,
      smJustify,
      mdJustify,
      lgJustify,
      xlJustify,
      xsAlignItems,
      smAlignItems,
      mdAlignItems,
      lgAlignItems,
      xlAlignItems,
      spaceY,
      xsSpaceY,
      smSpaceY,
      mdSpaceY,
      lgSpaceY,
      xlSpaceY,
      xxlSpaceY,
      fullWidth,
    ],
  );
  return <div className={classes}>{children}</div>;
};
